import allInfringements from './allInfringements.json';
import confirmedRemovals from './confirmedRemovals.json';
import searches from './searches.json';
import cidAssets from './cidAssets.json';
import cidClaims from './cidClaims.json';
import searchTypesData from './searchTypes.json';

function mockOk(data) {
  return {
    ok: true,
    status: 200,
    json: () => Promise.resolve(data),
    text: () => Promise.resolve(JSON.stringify(data)),
    headers: { get: () => 'application/json' },
  };
}

// Returns a mock response for a given API path when in demo mode.
// path arrives without the version prefix (e.g. 'results/all_results').
export default function getDemoResponse(path) {
  const p = path.replace(/\?.*$/, '');

  // Results
  if (/results\/all_results/.test(p)) return mockOk(allInfringements);
  if (/results\/successful_removals/.test(p)) return mockOk(confirmedRemovals);
  if (/results\/results_meta/.test(p)) {
    return mockOk({
      sort: {
        name: { options: [{ name: 'title', formatted: 'Infringing Title' }, { name: 'created_at', formatted: 'Found Date' }] },
        direction: { options: [{ name: 'asc', formatted: 'Ascending' }, { name: 'desc', formatted: 'Descending' }] },
      },
      filters: {
        options: [
          { name: 'title', formatted: 'Infringing Title', validPredicates: ['i_cont'] },
          {
            name: 'platform', formatted: 'Platform', validPredicates: ['cont_any'], validValues: [{ name: 'youtube', formatted: 'Youtube' }, { name: 'tiktok', formatted: 'TikTok' }, { name: 'instagram', formatted: 'Instagram' }, { name: 'facebook', formatted: 'Facebook' }],
          },
          { name: 'created_at', formatted: 'Found Date', validPredicates: ['gteq', 'lteq'] },
        ],
      },
    });
  }
  if (/results\/review\/stats/.test(p)) return mockOk({ positives: 47, reviewed: 41, pendingForCompany: 6 });
  if (/results\/review/.test(p)) {
    const editableOptions = [
      { api: 'pending', display: 'Pending' },
      { api: 'infringing-post', display: 'Send Takedown Notices' },
      { api: 'infringing-account', display: 'Infringing Account' },
      { api: 'legal-site-informational', display: 'Legal Site Informational' },
      { api: 'legal-site-distributor', display: 'Legal Site Distributor' },
      { api: 'wrong-content', display: 'Not An Infringement' },
    ];
    const makeRow = (id, title, url, platform) => ({
      id,
      cells: [
        { display: title, decoration: null, unit: null },
        {
          display: {
            url, postUrl: null, referenceUrl: null, secondaryUrl: null,
          },
          decoration: null,
          unit: null,
        },
        { display: platform, decoration: null, unit: null },
        {
          display: 'Pending', decoration: 'editable/dropdown', unit: null, api: 'pending', editable: 'dropdown', editableOptions, editableId: id,
        },
      ],
      domain: null,
      domainWithSubdomain: null,
      category: null,
      recordedAt: null,
      matchTimestamp: null,
      snapshotUrl: null,
      broadcasterName: null,
      embedUrl: null,
      referenceUrl: null,
    });
    return mockOk({
      data: [{
        label: null,
        type: 'review-table',
        feature: 'review/update',
        statusOptions: editableOptions,
        value: {
          header: [{ display: 'Title' }, { display: 'Infringing URL' }, { display: 'Platform' }, { display: 'Review Status' }],
          rows: [
            makeRow('64a1f2e3b4c5d6e7f8a9b001', 'Brand New Day - Full Track Repost', 'https://www.youtube.com/watch?v=dQw4w9WgXaA', 'Youtube'),
            makeRow('64a1f2e3b4c5d6e7f8a9b004', 'Dance Reel Using Copyrighted Audio', 'https://www.instagram.com/reel/CxDemo1234567', 'Instagram'),
            makeRow('64a1f2e3b4c5d6e7f8a9b005', 'Reaction Video - Full Song in Background', 'https://www.youtube.com/watch?v=abc123defDemo', 'Youtube'),
            makeRow('64a1f2e3b4c5d6e7f8a9b006', 'Full Album Compilation - All Tracks', 'https://www.youtube.com/watch?v=xyz789abcDemo', 'Youtube'),
            makeRow('64a1f2e3b4c5d6e7f8a9b008', 'Fan Tribute Video - Original Backing Track', 'https://www.facebook.com/video/987654321Demo', 'Facebook'),
            makeRow('64a1f2e3b4c5d6e7f8a9b009', 'Lyric Video - Unofficial Re-upload', 'https://www.youtube.com/watch?v=fan001abcDemo', 'Youtube'),
          ],
        },
      }],
    });
  }
  if (/results\/bulk/.test(p)) {
    return mockOk({
      singleCategoryConfig: [
        {
          category: 'content_creator',
          config: [
            {
              order: 1, key: 'title', label: 'Title', required: true, fieldName: 'Title', selectable: false, description: 'The title of the search to add result(s) to', fieldType: 'SINGLE_TEXT_INPUT',
            },
            {
              order: 2, key: 'page_url', label: 'Page Url', required: true, fieldName: 'Page Url', selectable: false, description: 'The URL of the page where the result was found', fieldType: 'SINGLE_TEXT_INPUT',
            },
            {
              order: 3,
              key: 'platform',
              label: 'Platform',
              required: true,
              fieldName: 'Platform',
              selectable: true,
              description: 'The platform/source where the result was found',
              fieldType: 'MULTI_SELECT',
              values: ['Facebook', 'Twitter', 'Instagram', 'Reddit', 'TikTok', 'Pinterest', 'LinkedIn', 'Tumblr', 'VK', 'DailyMotion', 'Quora', 'YouTube Videos', 'Telegram', 'Disqus', 'Blogspot', 'YouTube', 'YouTube Google', 'SoundCloud'],
              config: {
                supportedCategoryValues: {
                  contentCreator: ['Facebook', 'Twitter', 'Instagram', 'Reddit', 'Tiktok', 'Pinterest', 'Linkedin', 'Tumblr', 'Vk', 'Dailymotion', 'Quora', 'Youtube Videos', 'Telegram', 'Disqus', 'Blogspot', 'Twitch', 'Iptv', 'Kick', 'Youtube', 'Youtube Google', 'Telegram Google', 'Facebook Videos', 'Dailymotion Videos', 'Internet'],
                },
              },
            },
            {
              order: 4, key: 'detected_at', label: 'Detected At', required: false, fieldName: 'Detected At', selectable: false, description: 'Date that the content was detected (defaults to current date)', fieldType: 'DATETIME_ENTRY',
            },
            {
              order: 5, key: 'reference_url', label: 'Reference Url', required: false, fieldName: 'Reference Url', selectable: false, description: 'Reference/Original URL', fieldType: 'SINGLE_TEXT_INPUT',
            },
            {
              order: 6, key: 'post_url', label: 'Post Url', required: false, fieldName: 'Post Url', selectable: false, description: 'URL of the social media post', fieldType: 'SINGLE_TEXT_INPUT',
            },
            {
              order: 7, key: 'post_date', label: 'Post Date', required: false, fieldName: 'Post Date', selectable: false, description: 'Date of the social media post', fieldType: 'DATETIME_ENTRY',
            },
            {
              order: 8, key: 'view_count', label: 'View Count', required: false, fieldName: 'View Count', selectable: false, description: 'View count for the social media post', fieldType: 'SINGLE_TEXT_INPUT',
            },
            {
              order: 9, key: 'profile_link', label: 'Profile Link', required: false, fieldName: 'Profile Link', selectable: false, description: "Link to the author's profile", fieldType: 'SINGLE_TEXT_INPUT',
            },
            {
              order: 10, key: 'follower_count', label: 'Follower Count', required: false, fieldName: 'Follower Count', selectable: false, description: 'Number of followers the author has', fieldType: 'SINGLE_TEXT_INPUT',
            },
            {
              order: 15, key: 'infringing_title', label: 'Infringing Title', required: false, fieldName: 'Infringing Title', selectable: false, description: 'Title of the infringing content', fieldType: 'SINGLE_TEXT_INPUT',
            },
          ],
        },
      ],
      bulkConfig: [
        {
          order: 2, fieldName: 'Timezone', fieldType: 'TIMEZONE_SELECT', selectable: true, config: null, key: 'timezone', required: true, description: 'Select the timezone to use when interpreting datetime fields in the attached CSV', value: null,
        },
        {
          order: 3, fieldName: 'File', fieldType: 'SINGLE_MEDIA_UPLOAD', selectable: null, config: { acceptedFileType: ['.csv'] }, key: 'file', required: true, value: null,
        },
      ],
      headerMappings: [
        {
          category: 'content_creator',
          headers: [
            { name: 'title', required: true, description: 'The title of the search to add result(s) to' },
            { name: 'page_url', required: true, description: 'The URL of the page where the result was found' },
            { name: 'platform', required: true, description: 'The platform/source where the result was found (accepted values: Facebook, Twitter, Instagram, Reddit, TikTok, Pinterest, LinkedIn, Tumblr, VK, DailyMotion, Quora, YouTube Videos, Telegram, Disqus, Blogspot, YouTube, YouTube Google, SoundCloud)' },
            { name: 'detected_at', required: false, description: 'Date that the content was detected (defaults to current date)' },
            { name: 'reference_url', required: false, description: 'Reference/Original URL' },
            { name: 'post_url', required: false, description: 'URL of the social media post' },
            { name: 'post_date', required: false, description: 'Date of the social media post' },
            { name: 'view_count', required: false, description: 'View count for the social media post' },
            { name: 'profile_link', required: false, description: "Link to the author's profile" },
            { name: 'follower_count', required: false, description: 'Number of followers the author has' },
            { name: 'infringing_title', required: false, description: 'Title of the infringing content' },
          ],
        },
      ],
      categories: [
        { category: 'content_creator', display: 'Content Creator' },
      ],
    });
  }
  // Result detail — matches /results/:mongoId
  if (/^results\/[a-f0-9]{24}$/.test(p)) {
    const id = p.split('/').pop();
    return mockOk({
      id,
      searchId: 267868,
      companyId: 0,
      category: 'content_creator',
      pageUrl: 'https://www.tiktok.com/@demouser/video/7347389978860604704',
      title: 'Demo Infringing Content',
      postTitle: '#DemoContent #Unauthorized',
      platform: 'tiktok',
      createdAt: '2026-06-10T09:14:22.000Z',
      updatedAt: '2026-06-12T10:00:00.000Z',
      reviewStatus: 'infringing-post',
      reviewedAt: '2026-06-11T08:00:00.000Z',
      reviewerUserId: 0,
      reviewStartedAt: null,
      internalReviewStatus: 'approved',
      uploadSource: 'admin',
      postUrl: 'https://www.tiktok.com/@demouser/video/7347389978860604704',
      hasPostUrl: true,
      referenceUrl: 'https://www.youtube.com/watch?v=Y9p5YLvNt50',
      hasReferenceUrl: true,
      postMetrics: {
        id: 'demo-post-metrics-001',
        platform: 'tiktok',
        postId: 'https://www.tiktok.com/@demouser/video/7347389978860604704',
        createdTime: '2026-06-01T00:00:00.000Z',
        viewCount: 5569,
      },
      authorMetrics: {
        profileLink: 'https://www.tiktok.com/@demouser',
      },
      pageDomainBreakdown: {
        domain: 'tiktok.com',
        subdomain: 'www',
        domainWithSubdomain: 'www.tiktok.com',
        tld: 'com',
        sld: 'tiktok',
      },
      referenceDomainBreakdown: {
        domain: 'youtube.com',
        subdomain: 'www',
        domainWithSubdomain: 'www.youtube.com',
        tld: 'com',
        sld: 'youtube',
      },
      dmcaRounds: [
        {
          searchId: 267868,
          companyId: 0,
          dmcaTakedownHistories: [],
          status: 'in-progress',
          round: 1,
          category: 'content_creator',
          createdAt: '2026-06-11T08:00:00.000Z',
          updatedAt: '2026-06-11T08:00:00.000Z',
          id: 'demo_dmca_round_001',
        },
      ],
      downcheckingDetails: [
        {
          type: 'post',
          dmcaStatus: 'down',
          dmcaCheckedAt: '2026-06-12T10:00:00.000Z',
          dmcaDownAt: '2026-06-12T10:00:00.000Z',
          isDmcaChecking: false,
          isDomainDmcaChecking: false,
          id: 'demo_downcheck_001',
          additionalInfo: null,
        },
      ],
      filePath: null,
      language: null,
      matchTimestamp: null,
      recordedAt: null,
      fileName: null,
      reviewData: {
        isReviewable: false,
        reviewStatus: 'infringing-post',
        statusOptions: [
          { api: 'pending', display: 'Pending' },
          { api: 'infringing-post', display: 'Send Takedown Notices' },
          { api: 'infringing-account', display: 'Infringing Account' },
          { api: 'legal-site-informational', display: 'Legal Site Informational' },
          { api: 'legal-site-distributor', display: 'Legal Site Distributor' },
          { api: 'wrong-content', display: 'Not An Infringement' },
        ],
      },
    });
  }

  // Searches
  if (/searches\/meta/.test(p)) {
    return mockOk({
      scope: { options: [{ name: 'live', formatted: 'Live' }, { name: 'paused', formatted: 'Paused' }, { name: 'completed', formatted: 'Completed' }, { name: 'added', formatted: 'Added' }] },
      sort: {
        name: { options: [{ name: 'status', formatted: 'Status' }, { name: 'start_date', formatted: 'Start Date' }, { name: 'end_date', formatted: 'End Date' }, { name: 'result_count', formatted: 'Results' }] },
        direction: { options: [{ name: 'asc', formatted: 'Ascending' }, { name: 'desc', formatted: 'Descending' }] },
      },
      filters: {
        options: [
          { name: 'start_date', formatted: 'Start Date', validPredicates: ['gteq', 'lteq'] },
          { name: 'end_date', formatted: 'End Date', validPredicates: ['gteq', 'lteq'] },
          {
            name: 'status', formatted: 'Status', validPredicates: ['eq'], validValues: [{ name: 'scheduled', formatted: 'Scheduled' }, { name: 'running', formatted: 'Running' }, { name: 'paused', formatted: 'Paused' }, { name: 'completed', formatted: 'Completed' }, { name: 'canceled', formatted: 'Canceled' }, { name: 'failed', formatted: 'Failed' }],
          },
          { name: 'name', formatted: 'Name', validPredicates: ['i_cont'] },
          {
            name: 'platforms', formatted: 'Platforms', validPredicates: ['cont_any'], validValues: ['Facebook', 'Twitter', 'Instagram', 'TikTok', 'YouTube', 'YouTube Videos', 'SoundCloud'],
          },
          { name: 'result_count', formatted: 'Results', validPredicates: ['gteq', 'lteq'] },
        ],
      },
      status: { options: [{ name: 'scheduled', formatted: 'Scheduled' }, { name: 'running', formatted: 'Running' }, { name: 'paused', formatted: 'Paused' }, { name: 'completed', formatted: 'Completed' }, { name: 'canceled', formatted: 'Canceled' }, { name: 'failed', formatted: 'Failed' }] },
    });
  }
  if (/searches\/stats/.test(p)) {
    return mockOk({
      scheduled: 0, running: 3, paused: 0, completed: 1, canceled: 0, failed: 0,
    });
  }
  // Search detail — matches /searches/:numericId
  if (/^searches\/\d+$/.test(p)) {
    const searchId = p.split('/').pop();
    const searchNames = {
      100001: 'Brand Song Demo', 100002: 'Music Video Demo', 100003: 'Podcast Intro Track', 100004: 'Acoustic Version',
    };
    const name = searchNames[searchId] || 'Demo Search';
    return mockOk({
      query: { id: Number(searchId), paging: { to: '1', size: '10' } },
      search: {
        id: searchId,
        title: name,
        category: 'Content Creator',
        details: [],
        attributes: [
          {
            label: 'Status', value: 'Running', type: 'value', unit: null,
          },
          { label: 'Platforms', value: ['youtube', 'tiktok'], type: 'list' },
        ],
      },
      data: [
        {
          label: 'Total infringements', value: '12', type: 'value', unit: null,
        },
        {
          label: 'Infringements',
          type: 'review-table',
          feature: null,
          statusOptions: [
            { api: 'pending', display: 'Pending' },
            { api: 'infringing-post', display: 'Send Takedown Notices' },
            { api: 'infringing-account', display: 'Infringing Account' },
            { api: 'legal-site-informational', display: 'Legal Site Informational' },
            { api: 'legal-site-distributor', display: 'Legal Site Distributor' },
            { api: 'wrong-content', display: 'Not An Infringement' },
          ],
          value: {
            header: [
              { display: 'Infringing Title' },
              { display: 'Infringing URL' },
              { display: 'Platform' },
              { display: 'View Count' },
              { display: 'Takedown Notice Sent Date' },
              { display: 'Found Date' },
              { display: 'Review Status' },
            ],
            rows: [
              {
                id: '64a1f2e3b4c5d6e7f8a9d001',
                cells: [
                  { display: `${name} - Re-upload`, decoration: null, unit: null },
                  {
                    display: {
                      url: 'https://www.youtube.com/watch?v=searchDemo001', postUrl: null, linkUrl: null, referenceUrl: null,
                    },
                    decoration: null,
                    unit: null,
                  },
                  { display: 'Youtube', decoration: null, unit: null },
                  { display: '24800', decoration: null, unit: 'view' },
                  { display: null, decoration: 'datetime', unit: null },
                  { display: '2026-06-08 10:00:00 UTC', decoration: 'datetime', unit: null },
                  {
                    display: 'Pending', decoration: 'editable/dropdown', unit: null, api: 'pending', editable: 'dropdown', editableOptions: [{ api: 'pending', display: 'Pending' }, { api: 'infringing-post', display: 'Send Takedown Notices' }, { api: 'wrong-content', display: 'Not An Infringement' }], editableId: '64a1f2e3b4c5d6e7f8a9d001',
                  },
                ],
                domain: 'youtube.com',
                domainWithSubdomain: 'www.youtube.com',
                category: 'content_creator',
                recordedAt: null,
                matchTimestamp: null,
                snapshotUrl: null,
                broadcasterName: null,
                embedUrl: null,
                referenceUrl: null,
              },
              {
                id: '64a1f2e3b4c5d6e7f8a9d002',
                cells: [
                  { display: `${name} - Unofficial Clip`, decoration: null, unit: null },
                  {
                    display: {
                      url: 'https://www.tiktok.com/@user123/video/7560387345623000002', postUrl: null, linkUrl: null, referenceUrl: 'https://www.youtube.com/watch?v=Y9p5YLvNt50',
                    },
                    decoration: null,
                    unit: null,
                  },
                  { display: 'Tiktok', decoration: null, unit: null },
                  { display: '5200', decoration: null, unit: 'view' },
                  { display: '2026-06-09 14:00:00 UTC', decoration: 'datetime', unit: null },
                  { display: '2026-06-05 00:00:00 UTC', decoration: 'datetime', unit: null },
                  {
                    display: 'infringing-post', decoration: 'editable/dropdown', unit: null, api: 'infringing-post', editable: 'dropdown', editableOptions: [{ api: 'pending', display: 'Pending' }, { api: 'infringing-post', display: 'Send Takedown Notices' }, { api: 'wrong-content', display: 'Not An Infringement' }], editableId: '64a1f2e3b4c5d6e7f8a9d002',
                  },
                ],
                domain: 'tiktok.com',
                domainWithSubdomain: 'www.tiktok.com',
                category: 'content_creator',
                recordedAt: null,
                matchTimestamp: null,
                snapshotUrl: null,
                broadcasterName: null,
                embedUrl: null,
                referenceUrl: 'https://www.youtube.com/watch?v=Y9p5YLvNt50',
              },
            ],
          },
        },
      ],
    });
  }
  if (/searches/.test(p)) return mockOk(searches);

  // Content ID
  // Asset-scoped claims list (youtube-cid/assets/:assetId/claims)
  if (/youtube-cid\/assets\/[^/]+\/claims/.test(p)) {
    const assetId = p.split('/')[2];
    const filtered = cidClaims.data.filter((c) => c.assetId === assetId);
    return mockOk({ data: filtered, pagination: { totalResults: filtered.length, skip: 0, limit: 25 } });
  }
  // Claim history — must come before claim detail
  if (/youtube-cid\/claims\/[^/]+\/history/.test(p)) {
    return mockOk({
      kind: 'youtubePartner#claimHistory',
      id: '4vGV-5EgkVM',
      uploaderChannelId: 'UCxxx001Demo',
      event: [
        {
          kind: 'youtubePartner#claimEvent',
          time: '2026-06-12T15:00:00.000Z',
          source: {
            type: 'cms_user',
            contentOwnerId: 'g8kS_-BROu-1O1H8iFBxEA',
            userEmail: 'demo.admin@redflagai.co',
          },
          type: 'claim_update',
          typeDetails: { updateStatus: 'active' },
        },
        {
          kind: 'youtubePartner#claimEvent',
          time: '2026-06-10T09:14:22.000Z',
          type: 'claim_create_pending',
        },
      ],
    });
  }
  // Claim detail — must come before claims list
  if (/youtube-cid\/claims\/[^/]+$/.test(p)) {
    const claimId = p.split('/').pop();
    return mockOk({
      kind: 'youtubePartner#claim',
      id: claimId,
      assetId: 'A853540797122781',
      videoId: 'dQw4w9WgXaA',
      policy: {
        kind: 'youtubePartner#policy',
        id: 'S650235267251136',
        name: 'Monetize in all countries',
        rules: [{ action: 'monetize' }],
      },
      appliedPolicy: {
        kind: 'youtubePartner#policy',
        rules: [
          {
            action: 'track',
            conditions: {
              requiredTerritories: {
                type: 'include',
                territories: ['CU', 'IR', 'KP', 'SD', 'SY'],
              },
            },
          },
          { action: 'monetize' },
        ],
      },
      status: 'active',
      contentType: 'audiovisual',
      ugcType: 'standard',
      timeCreated: '2026-06-10T09:14:22.000Z',
      timeStatusLastModified: '2026-06-12T15:00:00.000Z',
      blockOutsideOwnership: false,
      matchInfo: {
        referenceId: 'gDQn_iBEEZU',
        longestMatch: { durationSecs: '52', userVideoOffset: '0', referenceOffset: '73' },
        totalMatch: { userVideoDurationSecs: '51', referenceDurationSecs: '51' },
        matchSegments: [
          {
            videoSegment: { kind: 'youtubePartner#segment', start: '250', duration: '49500' },
            referenceSegment: { kind: 'youtubePartner#segment', start: '73000', duration: '49500' },
            channel: 'Video',
          },
          {
            videoSegment: { kind: 'youtubePartner#segment', start: '0', duration: '51500' },
            referenceSegment: { kind: 'youtubePartner#segment', start: '73000', duration: '52000' },
            channel: 'Audio',
          },
        ],
      },
      origin: { source: 'videoMatch' },
      isPartnerUploaded: false,
      studioInfo: {
        claimUrl: `https://studio.youtube.com/video/dQw4w9WgXaA/claims/claims/${claimId}/edit`,
        videoUrl: 'https://studio.youtube.com/video/dQw4w9WgXaA/claims',
      },
    });
  }
  // Asset detail — must come before assets list
  if (/youtube-cid\/assets\/[^/]+$/.test(p)) {
    const assetId = p.split('/').pop();
    const assetTitles = {
      A853540797122781: 'Brand Song Demo - Full Track',
      A502868247413265: 'Music Video Demo - Official Cut',
      A652797667831046: 'Acoustic Version - Unplugged',
      A552314263361101: 'Podcast Intro Track - Full Version',
      A890308121603867: 'Brand Song - Instrumental Mix',
    };
    const title = assetTitles[assetId] || 'Demo Asset';
    return mockOk({
      kind: 'youtubePartner#asset',
      id: assetId,
      type: 'web',
      timeCreated: '2026-01-15T10:00:00.000Z',
      metadata: { title },
      metadataEffective: { title },
      label: ['Demo Artist'],
    });
  }
  if (/youtube-cid\/assets/.test(p)) return mockOk(cidAssets);
  if (/youtube-cid\/claims/.test(p)) {
    const qs = path.includes('?') ? path.slice(path.indexOf('?') + 1) : '';
    const assetId = new URLSearchParams(qs).get('assetId');
    if (assetId) {
      const filtered = cidClaims.data.filter((c) => c.assetId === assetId);
      return mockOk({ data: filtered, pagination: { totalResults: filtered.length, skip: 0, limit: 25 } });
    }
    return mockOk(cidClaims);
  }
  if (/youtube-cid\/whitelists/.test(p)) {
    return mockOk({
      kind: 'youtubePartner#whitelistList',
      items: [
        { kind: 'youtubePartner#whitelist', id: 'UCw791YE5ppNOG4N1_oD4xxQ', title: 'Demo Artist Official' },
        { kind: 'youtubePartner#whitelist', id: 'UCxxx002DemoChannel', title: 'Licensed Partner Channel' },
      ],
      pageInfo: { totalResults: 2, resultsPerPage: 25 },
    });
  }

  // Subscriptions
  if (/^subscriptions\/cancellation_status$/.test(p)) return mockOk({ cancellationRequestedAt: null, effectiveCancellationDate: null });
  if (/^subscriptions\/\d+$/.test(p)) {
    return mockOk({
      status: 'active',
      createdAt: 1704067200,
      currentPeriodStart: 1748736000,
      currentPeriodEnd: 1751328000,
      canceledDate: null,
      willCancelDate: null,
      productData: {
        name: 'Content Creator Pro',
        description: 'Full copyright protection for content creators',
        metadata: {
          searchLimit: '100',
          uid: 'content_creator_pro',
          verticalUid: 'content_creator',
        },
        prices: [
          {
            priceId: 'price_demo_monthly',
            currency: 'usd',
            unitAmount: 9900,
            metadata: {
              order: '1',
              title: 'Monthly',
              displayPlatforms: 'YouTube, TikTok, Instagram, Facebook',
              platforms: 'youtube,tiktok,instagram,facebook',
              description: 'Billed monthly',
            },
          },
        ],
      },
      cardData: {
        brand: 'visa',
        last4: '4242',
        type: 'card',
        expMonth: 12,
        expYear: 2027,
      },
    });
  }

  // Users
  if (/^users\/[^/]+\/reinvite$/.test(p)) return mockOk({ success: true });
  if (/^users\/invite_token$/.test(p)) return mockOk({ token: 'demo-invite-token-abc123', expiresAt: '2026-12-31T23:59:59.000Z' });
  if (/^users\/invited$/.test(p)) return mockOk({ success: true });
  if (/^users\/account$/.test(p)) {
    return mockOk({
      id: 0, email: 'demo@redflagai.co', firstName: 'Demo', lastName: 'User',
    });
  }
  if (/^users$/.test(p)) {
    return mockOk([
      {
        id: 0, firstName: 'Demo', lastName: 'User', email: 'demo@redflagai.co', status: 'active',
      },
      {
        id: 9998, firstName: 'Jane', lastName: 'Smith', email: 'jane.smith@demo.co', status: 'active',
      },
    ]);
  }

  // Account page — business info
  if (/customer-onboarding-payouts\/business-info/.test(p)) return mockOk({ country: 'United States of America', businessType: 'creator' });

  // Settings & misc — return safe empty values
  if (/maintenance_settings/.test(p)) return mockOk({ showStatusMessage: false, statusMessage: '', disabledPages: [] });
  if (/settings/.test(p)) return mockOk([]);
  if (/search-types\/bulk/.test(p)) {
    return mockOk({
      searchTypes: [{
        id: '14',
        category: 'content_creator',
        name: 'Content Creator',
        fieldData: [
          ...searchTypesData.sharedFieldData.filter((f) => ['recurrence', 'start_date_and_time', 'end_date_and_time', 'priority', 'platform', 'frequency'].includes(f.key)),
          ...searchTypesData.bulkOnlyFieldData,
        ],
        queueSteps: searchTypesData.queueSteps,
      }],
    });
  }
  if (/search-types/.test(p)) {
    return mockOk({
      searchTypes: [{
        id: '14',
        category: 'content_creator',
        name: 'Content Creator',
        fieldData: searchTypesData.sharedFieldData,
        queueSteps: searchTypesData.queueSteps,
      }],
    });
  }
  if (/customer-onboardings/.test(p)) return mockOk(null);

  // Mutations (POST/PUT/PATCH/DELETE) — silent success
  return mockOk({ success: true });
}
